import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { TextStyle } from "@tiptap/extension-text-style";
import { FontFamily } from "@tiptap/extension-font-family";
import { Extension } from "@tiptap/core";
import { useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { Button } from "./ui/button";
import {
  TextB, TextItalic, TextUnderline, TextStrikethrough,
  ListBullets, ListNumbers, Quotes, Link as LinkIcon, Image as ImageIcon,
  ArrowUUpLeft, ArrowUUpRight,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * TipTap-based rich text editor.
 * Emits HTML via onChange(html).
 * `attachTicketId` (optional): when set, image toolbar uploads to that ticket and inserts server URL.
 */

// Font size mark (inline style)
const FontSize = Extension.create({
  name: "fontSize",
  addOptions() { return { types: ["textStyle"] }; },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        fontSize: {
          default: null,
          parseHTML: el => el.style.fontSize?.replace(/["']+/g, "") || null,
          renderHTML: attrs => attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {},
        },
      },
    }];
  },
  addCommands() {
    return {
      setFontSize: size => ({ chain }) => chain().setMark("textStyle", { fontSize: size }).run(),
      unsetFontSize: () => ({ chain }) => chain().setMark("textStyle", { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

const FONT_FAMILIES = [
  { label: "Système", value: "" },
  { label: "IBM Plex Sans", value: "'IBM Plex Sans', sans-serif" },
  { label: "Manrope", value: "Manrope, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Arial", value: "Arial, sans-serif" },
  { label: "Courier", value: "'IBM Plex Mono', monospace" },
];

const FONT_SIZES = [
  { label: "Normal", value: "" },
  { label: "12", value: "12px" },
  { label: "14", value: "14px" },
  { label: "16", value: "16px" },
  { label: "18", value: "18px" },
  { label: "22", value: "22px" },
  { label: "28", value: "28px" },
];

export default forwardRef(function RichEditor({ value, onChange, placeholder, attachTicketId, minHeight = 140 }, ref) {
  const imgInput = useRef();
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: false, underline: false }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      Image.configure({ inline: false, allowBase64: true, HTMLAttributes: { class: "rounded-md max-w-full" } }),
      TextStyle,
      FontFamily.configure({ types: ["textStyle"] }),
      FontSize,
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none px-3 py-2",
        style: `min-height: ${minHeight}px;`,
        "data-testid": "rich-editor",
      },
      handleDrop: (view, event, slice, moved) => {
        if (moved) return false;
        const files = Array.from(event.dataTransfer?.files || []).filter((f) => f.type.startsWith("image/"));
        if (!files.length) return false;
        event.preventDefault();
        files.forEach((f) => insertImage(f));
        return true;
      },
      handlePaste: (view, event) => {
        const items = Array.from(event.clipboardData?.items || []);
        const imgs = items.filter((it) => it.kind === "file" && it.type.startsWith("image/"));
        if (!imgs.length) return false;
        event.preventDefault();
        imgs.forEach((it) => insertImage(it.getAsFile()));
        return true;
      },
    },
    onUpdate: ({ editor }) => { onChange?.(editor.getHTML()); },
  });

  // Sync external value changes (e.g. AI suggestion / macro insertion)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== undefined && value !== current && document.activeElement !== editor.view.dom) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  // Imperative API exposed to parents: insert HTML at the current cursor position.
  useImperativeHandle(ref, () => ({
    insertContent: (html) => {
      if (!editor) return;
      editor.chain().focus().insertContent(html).run();
    },
    focus: () => editor?.commands.focus(),
    getEditor: () => editor,
  }), [editor]);

  if (!editor) return <div className="border rounded-md bg-muted/20 h-24"/>;

  const btn = (active, onClick, testid, children, disabled = false) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`inline-flex items-center justify-center w-8 h-8 rounded text-sm ${
        active ? "bg-primary text-primary-foreground" : "hover:bg-muted text-foreground"
      } disabled:opacity-40`}
    >
      {children}
    </button>
  );

  const promptLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL du lien", prev || "https://");
    if (url === null) return;
    if (url === "") { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const insertImage = async (file) => {
    if (!file) return;
    if (attachTicketId) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post(`/tickets/${attachTicketId}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        // stream download endpoint returns file directly via GET /attachments/{id} — but we need a URL.
        // fetch as blob then embed as base64 to keep it simple.
        const dl = await api.get(`/attachments/${r.data.id}`, { responseType: "blob" });
        const reader = new FileReader();
        reader.onload = (ev) => {
          editor.chain().focus().setImage({ src: ev.target.result, alt: file.name }).run();
        };
        reader.readAsDataURL(dl.data);
        toast.success("Image insérée");
      } catch (e) {
        toast.error("Impossible d'insérer l'image");
      }
    } else {
      // No ticket — embed as base64
      const reader = new FileReader();
      reader.onload = (ev) => editor.chain().focus().setImage({ src: ev.target.result, alt: file.name }).run();
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="border rounded-md bg-background">
      <div className="flex flex-wrap items-center gap-0.5 border-b px-2 py-1.5 bg-muted/30" data-testid="rich-toolbar">
        {btn(false, () => editor.chain().focus().undo().run(), "rt-undo", <ArrowUUpLeft size={14}/>, !editor.can().undo())}
        {btn(false, () => editor.chain().focus().redo().run(), "rt-redo", <ArrowUUpRight size={14}/>, !editor.can().redo())}
        <span className="w-px h-5 bg-border mx-1"/>
        <select
          data-testid="rt-font"
          className="h-8 text-xs px-1 rounded bg-transparent hover:bg-muted"
          onChange={(e) => { const v = e.target.value; if (v) editor.chain().focus().setFontFamily(v).run(); else editor.chain().focus().unsetFontFamily().run(); }}
          value={editor.getAttributes("textStyle").fontFamily || ""}
        >
          {FONT_FAMILIES.map((f) => <option key={f.label} value={f.value}>{f.label}</option>)}
        </select>
        <select
          data-testid="rt-size"
          className="h-8 text-xs px-1 rounded bg-transparent hover:bg-muted"
          onChange={(e) => { const v = e.target.value; if (v) editor.chain().focus().setFontSize(v).run(); else editor.chain().focus().unsetFontSize().run(); }}
          value={editor.getAttributes("textStyle").fontSize || ""}
        >
          {FONT_SIZES.map((f) => <option key={f.label} value={f.value}>{f.label}</option>)}
        </select>
        <span className="w-px h-5 bg-border mx-1"/>
        {btn(editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), "rt-bold", <TextB size={14} weight="bold"/>)}
        {btn(editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), "rt-italic", <TextItalic size={14}/>)}
        {btn(editor.isActive("underline"), () => editor.chain().focus().toggleUnderline().run(), "rt-underline", <TextUnderline size={14}/>)}
        {btn(editor.isActive("strike"), () => editor.chain().focus().toggleStrike().run(), "rt-strike", <TextStrikethrough size={14}/>)}
        <span className="w-px h-5 bg-border mx-1"/>
        {btn(editor.isActive("heading", { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), "rt-h2", <b className="text-xs">H2</b>)}
        {btn(editor.isActive("heading", { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), "rt-h3", <b className="text-xs">H3</b>)}
        {btn(editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run(), "rt-ul", <ListBullets size={14}/>)}
        {btn(editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), "rt-ol", <ListNumbers size={14}/>)}
        {btn(editor.isActive("blockquote"), () => editor.chain().focus().toggleBlockquote().run(), "rt-quote", <Quotes size={14}/>)}
        {btn(editor.isActive("codeBlock"), () => editor.chain().focus().toggleCodeBlock().run(), "rt-code", <b className="text-xs mono">&lt;/&gt;</b>)}
        <span className="w-px h-5 bg-border mx-1"/>
        {btn(editor.isActive("link"), promptLink, "rt-link", <LinkIcon size={14}/>)}
        {btn(false, () => imgInput.current?.click(), "rt-image", <ImageIcon size={14}/>)}
        <input ref={imgInput} type="file" accept="image/*" className="hidden"
          onChange={(e) => { insertImage(e.target.files?.[0]); e.target.value = ""; }}/>
      </div>
      <EditorContent editor={editor} placeholder={placeholder}/>
    </div>
  );
});
